import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import MuteButton from '../ui/MuteButton';
import WalletButton from '../wallet/WalletButton';
import { useSeasonStore } from '../../stores/seasonStore';

const navItems = [
  { path: '/play', label: 'Play' },
  { path: '/leaderboard', label: 'Leaderboard' },
  { path: '/rules', label: 'Rules' },
  { path: '/transparency', label: 'Transparency' },
  { path: '/finals', label: 'Archive' },
];


export default function Header() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { phaseName, phaseType } = useSeasonStore();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            {/* Logo */}
            <Link to="/" className="relative group">
              <img
                src="/images/bg-logo.svg"
                alt="Beat Games"
                className="h-10 w-auto transition-all duration-300 group-hover:brightness-125"
              />
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-white/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-150" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium transition-all relative ${
                    location.pathname === item.path
                      ? 'text-white'
                      : 'text-white/40 hover:text-white'
                  }`}
                >
                  {item.label}
                  {location.pathname === item.path && (
                    <span className="absolute bottom-0 left-4 right-4 h-px bg-white" />
                  )}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Live indicator */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]">
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${phaseType === 'CLOSED' ? 'bg-white/40' : 'bg-[#22c55e]'} opacity-75`} />
                <span className={`relative inline-flex rounded-full h-2 w-2 ${phaseType === 'CLOSED' ? 'bg-white/40' : 'bg-[#22c55e]'}`} />
              </span>
              <span className="text-xs font-medium text-white/60">Season 1 · {phaseName}</span>
            </div>

            {/* Mute button */}
            <MuteButton />

            {/* Custom wallet button with $BG balance */}
            <WalletButton />

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-11 h-11 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-all"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Menu panel */}
        <nav
          className={`absolute top-16 left-0 right-0 bg-[#0a0a0a] border-b border-white/[0.06] transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-4 rounded-lg text-base font-medium transition-all ${
                  location.pathname === item.path
                    ? 'text-white bg-white/[0.05]'
                    : 'text-white/60 hover:text-white hover:bg-white/[0.03]'
                }`}
              >
                {location.pathname === item.path && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                )}
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile live indicator */}
          <div className="px-4 pb-4 sm:hidden">
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03]">
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${phaseType === 'CLOSED' ? 'bg-white/40' : 'bg-[#22c55e]'} opacity-75`} />
                <span className={`relative inline-flex rounded-full h-2 w-2 ${phaseType === 'CLOSED' ? 'bg-white/40' : 'bg-[#22c55e]'}`} />
              </span>
              <span className="text-sm font-medium text-white/60">Season 1 · {phaseName}</span>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}
